import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Chip,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  School,
  LocationOn,
  Phone,
  Email,
  Menu as MenuIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Grade as GradeIcon,
  Info as InfoIcon,
  AttachMoney,
  Dashboard,
  Gavel,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const schoolColors = {
  primary: '#0d233a',     // Deep Navy
  primaryLight: '#1a365d',
  secondary: '#00796b',   // Teal Green Accent
  accent: '#e65100',      // Deep Orange
  gold: '#f59e0b',
  dark: '#0a1128',
  light: '#f8fafc',
  white: '#ffffff',
  green: '#15803d',
};

const navLinks = [
  { label: 'Home', path: '/', icon: <School />, category: 'main' },
  { label: 'About Us', path: '/about', icon: <InfoIcon />, category: 'main' },
  { label: 'Sec Apply (Form 1–6)', path: '/apply', icon: <School />, category: 'admission', highlight: 'sec' },
  { label: 'Pri Apply (English Medium)', path: '/primary/apply', icon: <School />, category: 'admission', highlight: 'pri' },
  { label: 'Track App', path: '/track', icon: <VisibilityIcon />, category: 'services' },
  { label: 'Results', path: '/results', icon: <GradeIcon />, category: 'services', badge: 'new' },
  { label: 'Fee Structure', path: '/fees', icon: <AttachMoney />, category: 'info' },
  { label: 'School Rules', path: '/rules', icon: <Gavel />, category: 'info' },
  { label: 'Admin Portal', path: '/admin/login', icon: <Dashboard />, category: 'admin', outlined: true },
];

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isPrimarySection = location.pathname.startsWith('/primary');

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <>
      {/* ── Top Bar with Official School Contacts & Reg Numbers ── */}
      <Box sx={{
        bgcolor: isPrimarySection ? '#14532d' : schoolColors.dark,
        color: 'white',
        py: 0.8,
        px: { xs: 1.5, md: 3 },
        fontSize: '0.8rem',
        borderBottom: '1px solid rgba(245, 158, 11, 0.3)',
        display: { xs: 'none', sm: 'block' },
        transition: 'background-color 0.3s ease'
      }}>
        <Container maxWidth="xl" disableGutters>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            {/* Location & Reg info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOn sx={{ fontSize: 16, color: schoolColors.gold }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 600 }}>
                  P.O. BOX 188, KALENGAKELU, MLIMBA - MOROGORO
                </Typography>
              </Box>
              <Chip
                label={isPrimarySection ? "REG NO: EM.21666 | EXAM: PS11011193" : "REG NO: S.4913 | NECTA: S.5430 | TIN: 118-249-437"}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  bgcolor: 'rgba(245, 158, 11, 0.2)',
                  color: schoolColors.gold,
                  border: '1px solid rgba(245, 158, 11, 0.4)'
                }}
              />
            </Box>

            {/* Contacts */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Phone sx={{ fontSize: 15, color: schoolColors.gold }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 600 }}>
                  {isPrimarySection ? '+255 787 705 910 / +255 767 705 910 / +255 655 705 910' : '+255 784 268 575 / +255 767 268 575 / +255 622 268 575'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Email sx={{ fontSize: 15, color: schoolColors.gold }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 600 }}>
                  {isPrimarySection ? 'kaozyaenglishmediumschool@gmail.com' : 'kaozyahighsch@gmail.com'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Main Sticky Navigation Bar with Official Heading & Emblem ── */}
      <AppBar
        position="sticky"
        elevation={scrolled ? 4 : 1}
        sx={{
          bgcolor: schoolColors.white,
          color: schoolColors.primary,
          transition: 'all 0.3s ease',
          borderBottom: '2px solid rgba(13, 35, 58, 0.1)',
          zIndex: 1100,
        }}
      >
        <Toolbar sx={{ px: { xs: 1.5, md: 3 }, py: { xs: 0.8, md: 1 } }}>
          {/* Brand Logo Emblem & Official School Name */}
          <Box
            onClick={() => navigate(isPrimarySection ? '/primary/apply' : '/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mr: { xs: 1, md: 3 },
              textDecoration: 'none'
            }}
          >
            {/* Official Circular Emblem */}
            <Box
              sx={{
                width: { xs: 52, md: 62 },
                height: { xs: 52, md: 62 },
                borderRadius: '50%',
                overflow: 'hidden',
                border: isPrimarySection ? '2px solid #15803d' : '2px solid #0d233a',
                bgcolor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: { xs: 1.2, md: 1.8 },
                flexShrink: 0,
                boxShadow: '0 4px 12px rgba(13, 35, 58, 0.2)',
              }}
            >
              <Box
                component="img"
                src={isPrimarySection ? "/images/kaozya-english-medium-header.jpg" : "/images/kaozya-official-header.jpg"}
                alt={isPrimarySection ? "Kaozya English Medium School Logo" : "Kaozya High School Logo"}
                sx={{
                  width: isPrimarySection ? '380%' : '320%',
                  height: 'auto',
                  objectFit: 'cover',
                  objectPosition: 'left center',
                  transform: isPrimarySection ? 'scale(1.2) translateX(36%)' : 'scale(1.15) translateX(32%)',
                }}
              />
            </Box>

            {/* Official School Title */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 900,
                  color: isPrimarySection ? '#14532d' : '#0d233a',
                  lineHeight: 1.05,
                  fontSize: { xs: '1.05rem', sm: '1.25rem', md: '1.45rem' },
                  letterSpacing: '0.4px',
                  fontFamily: '"Montserrat", "Roboto", "Helvetica", sans-serif',
                }}
              >
                {isPrimarySection ? 'KAOZYA ENGLISH MEDIUM SCHOOL' : 'KAOZYA HIGH SCHOOL'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isPrimarySection ? '#166534' : '#e65100',
                  fontWeight: 800,
                  fontSize: { xs: '0.68rem', sm: '0.78rem' },
                  display: 'block',
                  lineHeight: 1.2,
                  mt: 0.2,
                  letterSpacing: '0.2px'
                }}
              >
                {isPrimarySection ? 'P.O. BOX 188, KALENGAKELU, MLIMBA-MOROGORO' : 'KALENGAKELU, MLIMBA - MOROGORO • REG: S.4913'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#00796b',
                  fontWeight: 700,
                  fontSize: { xs: '0.62rem', sm: '0.7rem' },
                  fontStyle: 'italic',
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                "QUALITY EDUCATION FOR A BETTER FUTURE"
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Desktop Navigation Links */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
              {/* Main Links */}
              {navLinks.filter(l => l.category === 'main').map(({ label, path, icon }) => {
                const active = isActive(path);
                return (
                  <Button
                    key={label}
                    onClick={() => navigate(path)}
                    startIcon={icon}
                    sx={{
                      textTransform: 'none',
                      fontWeight: active ? 800 : 600,
                      fontSize: 13.5,
                      color: active ? schoolColors.primary : '#334155',
                      bgcolor: active ? 'rgba(13, 35, 58, 0.08)' : 'transparent',
                      borderBottom: active ? `3px solid ${schoolColors.accent}` : '3px solid transparent',
                      borderRadius: '6px 6px 0 0',
                      px: 1.5,
                      py: 0.8,
                      '&:hover': { bgcolor: 'rgba(13, 35, 58, 0.05)' },
                    }}
                  >
                    {label}
                  </Button>
                );
              })}

              {/* Secondary Apply Button */}
              <Button
                variant="contained"
                onClick={() => navigate('/apply')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: 13,
                  bgcolor: schoolColors.accent,
                  color: 'white',
                  borderRadius: 2,
                  px: 1.8,
                  boxShadow: isActive('/apply') ? `0 0 0 2px ${schoolColors.gold}` : 'none',
                  '&:hover': { bgcolor: '#c2410c' },
                }}
                startIcon={<School />}
              >
                Sec Apply
              </Button>

              {/* Primary Apply Button */}
              <Button
                variant="contained"
                onClick={() => navigate('/primary/apply')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: 13,
                  bgcolor: schoolColors.green,
                  color: 'white',
                  borderRadius: 2,
                  px: 1.8,
                  boxShadow: isActive('/primary/apply') ? `0 0 0 2px ${schoolColors.gold}` : 'none',
                  '&:hover': { bgcolor: '#166534' },
                }}
                startIcon={<School />}
              >
                Pri Apply
              </Button>

              {/* Services & Info links */}
              {navLinks.filter(l => l.category === 'services' || l.category === 'info').map(({ label, path, icon, badge }) => {
                const active = isActive(path);
                return (
                  <Button
                    key={label}
                    onClick={() => navigate(path)}
                    startIcon={
                      badge ? (
                        <Badge badgeContent={badge} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16 } }}>
                          {icon}
                        </Badge>
                      ) : (
                        icon
                      )
                    }
                    sx={{
                      textTransform: 'none',
                      fontWeight: active ? 800 : 600,
                      fontSize: 13,
                      color: active ? schoolColors.primary : '#475569',
                      borderRadius: 1.5,
                      px: 1.2,
                      py: 0.6,
                      bgcolor: active ? 'rgba(13, 35, 58, 0.06)' : 'transparent',
                      '&:hover': { bgcolor: 'rgba(13, 35, 58, 0.04)' },
                    }}
                  >
                    {label}
                  </Button>
                );
              })}

              {/* Admin Portal Button */}
              <Button
                variant="outlined"
                onClick={() => navigate('/admin/login')}
                startIcon={<Dashboard />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: 13,
                  color: schoolColors.primary,
                  borderColor: schoolColors.primary,
                  borderRadius: 2,
                  px: 1.6,
                  ml: 0.5,
                  '&:hover': { bgcolor: 'rgba(13, 35, 58, 0.06)', borderColor: schoolColors.primary },
                }}
              >
                Admin
              </Button>
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              edge="end"
              onClick={() => setDrawerOpen(true)}
              sx={{ color: schoolColors.primary, ml: 1 }}
            >
              <MenuIcon fontSize="large" />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* ── Mobile Navigation Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            bgcolor: '#ffffff',
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: schoolColors.primary, color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                overflow: 'hidden',
                bgcolor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #f59e0b'
              }}
            >
              <Box
                component="img"
                src="/images/kaozya-official-header.jpg"
                alt="Kaozya Logo"
                sx={{
                  width: '320%',
                  height: 'auto',
                  objectFit: 'cover',
                  objectPosition: 'left center',
                  transform: 'scale(1.15) translateX(32%)',
                }}
              />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                KAOZYA HIGH SCHOOL
              </Typography>
              <Typography variant="caption" sx={{ color: schoolColors.gold, fontWeight: 700 }}>
                REG NO: S.4913
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <List sx={{ px: 1.5, py: 2 }}>
          {navLinks.map(({ label, path, icon, highlight, outlined }) => {
            const active = isActive(path);
            return (
              <ListItem
                button
                key={label}
                onClick={() => {
                  navigate(path);
                  setDrawerOpen(false);
                }}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  bgcolor: active
                    ? 'rgba(13, 35, 58, 0.1)'
                    : highlight === 'sec'
                    ? 'rgba(230, 81, 0, 0.1)'
                    : highlight === 'pri'
                    ? 'rgba(21, 128, 61, 0.1)'
                    : 'transparent',
                  color: active
                    ? schoolColors.primary
                    : highlight === 'sec'
                    ? schoolColors.accent
                    : highlight === 'pri'
                    ? schoolColors.green
                    : '#1e293b',
                  fontWeight: active || highlight ? 800 : 600,
                  border: outlined ? `1px solid ${schoolColors.primary}` : 'none',
                }}
              >
                <ListItemIcon
                  sx={{
                    color: active
                      ? schoolColors.primary
                      : highlight === 'sec'
                      ? schoolColors.accent
                      : highlight === 'pri'
                      ? schoolColors.green
                      : '#64748b',
                    minWidth: 38,
                  }}
                >
                  {icon}
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    fontSize: '0.92rem',
                    fontWeight: active || highlight ? 800 : 600,
                  }}
                />
              </ListItem>
            );
          })}
        </List>

        <Box sx={{ mt: 'auto', p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5, fontWeight: 700 }}>
            Official Contacts:
          </Typography>
          <Typography variant="caption" sx={{ color: '#0f172a', display: 'block', fontWeight: 600 }}>
            📞 +255 784 268 575 / +255 767 268 575
          </Typography>
          <Typography variant="caption" sx={{ color: '#0f172a', display: 'block', fontWeight: 600 }}>
            ✉️ kaozyahighsch@gmail.com
          </Typography>
        </Box>
      </Drawer>
    </>
  );
};

export default Navigation;
